import React, { useState } from "react";
import "./Sidebar.scss";
import { NavLink as RouterLink, useNavigate, useLocation } from "react-router-dom";
import Logo from "../../assets/images/logo/Claimtec-logo.png";

import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";
import useAuth from "../../hooks/useAuth";
import useConfig from "../../hooks/useConfig";
import { BiSolidDashboard } from "react-icons/bi";
import * as Storage from "../../store/LocalStorage";
import { TbVersions, TbReportAnalytics } from "react-icons/tb";
import { HiUsers } from "react-icons/hi2";
import { MdMiscellaneousServices, MdLogout } from "react-icons/md";
import { FaAngleDown, FaHome, FaCar, FaGavel, FaMoneyBillWave, FaIdCard } from "react-icons/fa";
import { IoMdCheckmarkCircle, IoMdTime, IoMdCloseCircle } from "react-icons/io";

export default function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const config = useConfig();
  const [showServicesSubMenu, setShowServicesSubMenu] = useState(false);
  const [showRoadsideAssistanceSubMenu, setShowRoadsideAssistanceSubMenu] = useState(false);

  const onLogout = () => {
    logout();
    Storage.remove(config.sessionKey);
    console.log("Session Cleared");
  };

  // Check if a path is active
  const isActive = (path) => {
    return location.pathname === path;
  };

  // Check if any submenu item is active
  const isServicesActive = () => {
    const servicesPaths = [
      '/services',
      '/home-assist',
      '/roadside/completed-services',
      '/roadside/inprogress-services',
      '/roadside/canceled-services',
      '/driver-assist',
      '/legal-assist',
      '/fines',
      '/license'
    ];
    return servicesPaths.some(path => location.pathname === path);
  };

  // Auto-expand Services menu if on a services page
  React.useEffect(() => {
    if (isServicesActive()) {
      setShowServicesSubMenu(true);
    }
  }, [location.pathname]);

  return (
    <SimpleBar className="sidebar-main" autoHide={false}>
      <div className="sidebar-logo">
        <img src={Logo} className="crown-img" alt="ClaimTec Logo" />
        <div className="logo-divider"></div>
      </div>

      <div className="sidebar-menu-list">
        {/* Dashboard */}
        <div className="each-icon-div">
          <RouterLink
            id="dashboard"
            className={`each-icon ${isActive('/dashboard') ? 'active' : ''}`}
            to="/dashboard"
          >
            <BiSolidDashboard size={20} />
            <span>Dashboard</span>
          </RouterLink>
        </div>

        {/* Users */}
        <div className="each-icon-div">
          <RouterLink
            id="users-list"
            className={`each-icon ${isActive('/users-list') ? 'active' : ''}`}
            to="/users-list"
          >
            <HiUsers size={20} />
            <span>Users</span>
          </RouterLink>
        </div>

        {/* Reports */}
        <div className="each-icon-div">
          <RouterLink
            id="reports"
            className={`each-icon ${isActive('/reports') ? 'active' : ''}`}
            to="/reports"
          >
            <TbReportAnalytics size={20} />
            <span>Reports</span>
          </RouterLink>
        </div>

        {/* Services Menu */}
        <div className="each-icon-div">
          <div
            id="services"
            className={`each-icon ${showServicesSubMenu || isServicesActive() ? "active" : ""}`}
            onClick={() => setShowServicesSubMenu(!showServicesSubMenu)}
          >
            <MdMiscellaneousServices size={20} />
            <span>Services</span>
            <FaAngleDown
              size={16}
              className={`dropdown-icon ${showServicesSubMenu ? "rotate" : ""}`}
            />
          </div>

          {/* Services Submenu */}
          <div className={`submenu ${showServicesSubMenu ? "show" : ""}`}>
            <RouterLink
              className={`submenu-item ${isActive('/services') ? 'active' : ''}`}
              to="/services"
            >
              <div className="submenu-dot"></div>
              <span>All Services</span>
            </RouterLink>

            <RouterLink
              className={`submenu-item ${isActive('/home-assist') ? 'active' : ''}`}
              to="/home-assist"
            >
              <FaHome size={16} />
              <span>Home Assist</span>
            </RouterLink>

            {/* Roadside Assistance Submenu */}
            <div
              className={`submenu-item expandable ${
                showRoadsideAssistanceSubMenu ? "active" : ""
              }`}
              onClick={(e) => {
                e.stopPropagation();
                setShowRoadsideAssistanceSubMenu(!showRoadsideAssistanceSubMenu);
              }}
            >
              <FaCar size={16} />
              <span>Roadside Assistance</span>
              <FaAngleDown
                size={14}
                className={`dropdown-icon ${showRoadsideAssistanceSubMenu ? "rotate" : ""}`}
              />
            </div>

            {/* Roadside Submenu Items */}
            <div className={`roadside-submenu ${showRoadsideAssistanceSubMenu ? "show" : ""}`}>
              <RouterLink
                className={`roadside-item ${isActive('/roadside/completed-services') ? 'active' : ''}`}
                to="/roadside/completed-services"
              >
                <IoMdCheckmarkCircle size={16} />
                <span>Completed</span>
              </RouterLink>

              <RouterLink
                className={`roadside-item ${isActive('/roadside/inprogress-services') ? 'active' : ''}`}
                to="/roadside/inprogress-services"
              >
                <IoMdTime size={16} />
                <span>In Progress</span>
              </RouterLink>

              <RouterLink
                className={`roadside-item ${isActive('/roadside/canceled-services') ? 'active' : ''}`}
                to="/roadside/canceled-services"
              >
                <IoMdCloseCircle size={16} />
                <span>Cancelled</span>
              </RouterLink>
            </div>

            <RouterLink
              className={`submenu-item ${isActive('/driver-assist') ? 'active' : ''}`}
              to="/driver-assist"
            >
              <FaCar size={16} />
              <span>Driver Assist</span>
            </RouterLink>

            <RouterLink
              className={`submenu-item ${isActive('/legal-assist') ? 'active' : ''}`}
              to="/legal-assist"
            >
              <FaGavel size={16} />
              <span>Legal Assist</span>
            </RouterLink>

            <RouterLink
              className={`submenu-item ${isActive('/fines') ? 'active' : ''}`}
              to="/fines"
            >
              <FaMoneyBillWave size={16} />
              <span>Fines</span>
            </RouterLink>

            <RouterLink
              className={`submenu-item ${isActive('/license') ? 'active' : ''}`}
              to="/license"
            >
              <FaIdCard size={16} />
              <span>License Renewals</span>
            </RouterLink>
          </div>
        </div>

        {/* App Version */}
        <div className="each-icon-div">
          <RouterLink
            id="app-version"
            className={`each-icon ${isActive('/app-version') ? 'active' : ''}`}
            to="/app-version"
          >
            <TbVersions size={20} />
            <span>App Version</span>
          </RouterLink>
        </div>

        {/* Logout */}
        <div className="each-icon-div logout-section">
          <div className="logout-divider"></div>
          <div
            id="logout"
            className="each-icon logout-btn"
            onClick={onLogout}
          >
            <MdLogout size={20} />
            <span>Logout</span>
          </div>
        </div>
      </div>
    </SimpleBar>
  );
}